import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { useFirebase } from "../../firebase/userContext";
import { FALLBACK_PROFILE_IMAGE, PORTFOLIO_UID } from "../../config/siteContent";
import "./AdminPage.css";

const EMPTY_PAPER = {
  id: "",
  title: "",
  authors: "",
  publishedOn: "",
  abstract: "",
  image: "",
  paperLink: "",
  arvixLink: "",
  sourceCodeLink: "",
  presentationSlidesLink: "",
  presentationVideoLink: "",
};

const EMPTY_BIO = {
  name: "",
  credentialTitle: "",
  position: "",
  location: "",
  description: "",
  profile_picture: "",
  profile_logo: "",
  copyrightLogo: "",
  email: "",
  linkedIn: "",
  github: "",
  twitter: "",
  collegeId: "",
  googleScholar: "",
  bookingLink: "",
  substackLink: "",
  cvLink: "",
  currentProjectLink: "",
  aboutHeading: "",
  aboutIntro: "",
  aboutParagraphsText: "",
  focusAreasText: "",
  aboutImage: "",
};

function toSlug(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function normalizeBio(data) {
  const bio = { ...EMPTY_BIO, ...(data || {}) };

  return {
    ...bio,
    aboutParagraphsText: Array.isArray(bio.aboutParagraphs)
      ? bio.aboutParagraphs.join("\n\n")
      : bio.aboutParagraphsText || "",
    focusAreasText: Array.isArray(bio.focusAreas)
      ? bio.focusAreas.join("\n")
      : bio.focusAreasText || "",
  };
}

function normalizePapers(data) {
  if (!data) {
    return [];
  }

  return Object.entries(data).map(([id, value]) => ({
    ...EMPTY_PAPER,
    ...value,
    id,
  }));
}

function serializeBio(formState) {
  const aboutParagraphs = formState.aboutParagraphsText
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  const focusAreas = formState.focusAreasText
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

  return {
    name: formState.name.trim(),
    credentialTitle: formState.credentialTitle.trim(),
    position: formState.position.trim(),
    location: formState.location.trim(),
    description: formState.description.trim(),
    profile_picture: formState.profile_picture.trim(),
    profile_logo: formState.profile_logo.trim(),
    copyrightLogo: formState.copyrightLogo.trim(),
    email: formState.email.trim(),
    linkedIn: formState.linkedIn.trim(),
    github: formState.github.trim(),
    twitter: formState.twitter.trim(),
    collegeId: formState.collegeId.trim(),
    googleScholar: formState.googleScholar.trim(),
    bookingLink: formState.bookingLink.trim(),
    substackLink: formState.substackLink.trim(),
    cvLink: formState.cvLink.trim(),
    currentProjectLink: formState.currentProjectLink.trim(),
    aboutHeading: formState.aboutHeading.trim(),
    aboutIntro: formState.aboutIntro.trim(),
    aboutParagraphs,
    focusAreas,
    aboutImage: formState.aboutImage.trim(),
  };
}

function serializePapers(papers) {
  return papers.reduce((acc, paper) => {
    const title = paper.title.trim();

    if (!title) {
      return acc;
    }

    const id = paper.id || toSlug(title) || `paper-${Date.now()}`;
    acc[id] = {
      title,
      authors: paper.authors.trim(),
      publishedOn: paper.publishedOn.trim(),
      abstract: paper.abstract.trim(),
      image: paper.image.trim(),
      paperLink: paper.paperLink.trim(),
      arvixLink: paper.arvixLink.trim(),
      sourceCodeLink: paper.sourceCodeLink.trim(),
      presentationSlidesLink: paper.presentationSlidesLink.trim(),
      presentationVideoLink: paper.presentationVideoLink.trim(),
    };
    return acc;
  }, {});
}

function Field({
  label,
  name,
  value,
  onChange,
  as = "input",
  rows = 3,
  placeholder = "",
  type = "text",
}) {
  return (
    <Form.Group className="admin-field">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        as={as}
        type={as === "textarea" ? undefined : type}
        rows={as === "textarea" ? rows : undefined}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </Form.Group>
  );
}

function UploadField({ label, onSelect, uploading, uploadLabel = "Upload image" }) {
  return (
    <Form.Group className="admin-field">
      <Form.Label>{label}</Form.Label>
      <div className="admin-upload-row">
        <Form.Control type="file" accept="image/*" onChange={onSelect} />
        <span className="admin-upload-status">
          {uploading ? "Uploading..." : uploadLabel}
        </span>
      </div>
    </Form.Group>
  );
}

export default function AdminPage() {
  const firebase = useFirebase();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [bioForm, setBioForm] = useState(EMPTY_BIO);
  const [papersForm, setPapersForm] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState("");

  const isAuthorized = firebase.authUser?.uid === PORTFOLIO_UID;
  const previewImage = bioForm.profile_picture || FALLBACK_PROFILE_IMAGE;

  useEffect(() => {
    if (!isAuthorized) {
      setLoading(false);
      return;
    }

    const handleBio = (data) => {
      setBioForm(normalizeBio(data));
      setLoading(false);
    };
    const handlePapers = (data) => {
      setPapersForm(normalizePapers(data));
    };

    firebase.getUsersBio(PORTFOLIO_UID, handleBio);
    firebase.getPapers(PORTFOLIO_UID, handlePapers);
  }, [firebase, isAuthorized]);

  const paperCountLabel = useMemo(
    () => `${papersForm.filter((item) => item.title.trim()).length} saved`,
    [papersForm]
  );

  const handleLogin = async (event) => {
    event.preventDefault();
    setAuthError("");

    try {
      await firebase.signInAdmin(email.trim(), password);
    } catch (error) {
      setAuthError(error.message || "Unable to sign in.");
    }
  };

  const handleBioChange = (event) => {
    const { name, value } = event.target;
    setBioForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handlePaperChange = (index, event) => {
    const { name, value } = event.target;
    setPapersForm((current) =>
      current.map((paper, paperIndex) =>
        paperIndex === index ? { ...paper, [name]: value } : paper
      )
    );
  };

  const addPaper = () => {
    setPapersForm((current) => [...current, { ...EMPTY_PAPER, id: `draft-${Date.now()}` }]);
  };

  const removePaper = (index) => {
    setPapersForm((current) => current.filter((_, paperIndex) => paperIndex !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage("");

    try {
      await firebase.saveUsersBio(PORTFOLIO_UID, serializeBio(bioForm));
      await firebase.savePapers(PORTFOLIO_UID, serializePapers(papersForm));
      setSaveMessage("Changes saved to Firebase.");
    } catch (error) {
      setSaveMessage(error.message || "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const uploadBioImage = async (fieldName, file, folder) => {
    if (!file) {
      return;
    }

    setUploadingField(fieldName);
    setSaveMessage("");

    try {
      const url = await firebase.uploadImage(file, folder);
      setBioForm((current) => ({
        ...current,
        [fieldName]: url,
      }));
      setSaveMessage("Image uploaded. Save changes to publish it.");
    } catch (error) {
      setSaveMessage(error.message || "Image upload failed.");
    } finally {
      setUploadingField("");
    }
  };

  const uploadPaperImage = async (index, file) => {
    if (!file) {
      return;
    }

    const uploadKey = `paper-${index}`;
    setUploadingField(uploadKey);
    setSaveMessage("");

    try {
      const url = await firebase.uploadImage(file, "papers");
      setPapersForm((current) =>
        current.map((paper, paperIndex) =>
          paperIndex === index ? { ...paper, image: url } : paper
        )
      );
      setSaveMessage("Paper image uploaded. Save changes to publish it.");
    } catch (error) {
      setSaveMessage(error.message || "Image upload failed.");
    } finally {
      setUploadingField("");
    }
  };

  if (!isAuthorized) {
    return (
      <div className="admin-shell">
        <Container className="admin-login-wrap">
          <div className="admin-login-card">
            <p className="admin-eyebrow">Admin</p>
            <h1>Sign in to edit your portfolio</h1>
            <p className="admin-support-copy">
              Use the Firebase-authenticated account that owns this portfolio.
            </p>
            <Form onSubmit={handleLogin}>
              <Field
                label="Email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <Field
                label="Password"
                name="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
              />
              {authError ? <Alert variant="danger">{authError}</Alert> : null}
              <Button type="submit" className="admin-primary-btn">
                Sign in
              </Button>
            </Form>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <Container className="admin-page">
        <div className="admin-topbar">
          <div>
            <p className="admin-eyebrow">Portfolio admin</p>
            <h1>Edit links, about content, and research cards</h1>
          </div>
          <div className="admin-topbar-actions">
            <Button
              variant="outline-dark"
              className="admin-secondary-btn"
              onClick={firebase.signOutAdmin}
            >
              Sign out
            </Button>
            <Button className="admin-primary-btn" disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>

        {saveMessage ? (
          <Alert variant={saveMessage.includes("saved") ? "success" : "danger"}>
            {saveMessage}
          </Alert>
        ) : null}

        <div className="admin-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setActiveTab("profile")}
          >
            Profile and links
          </button>
          <button
            className={activeTab === "about" ? "active" : ""}
            onClick={() => setActiveTab("about")}
          >
            About page
          </button>
          <button
            className={activeTab === "papers" ? "active" : ""}
            onClick={() => setActiveTab("papers")}
          >
            Papers
          </button>
        </div>

        {loading ? <p className="admin-loading">Loading content...</p> : null}

        {!loading && activeTab === "profile" ? (
          <Row className="admin-grid">
            <Col lg={7}>
              <div className="admin-panel">
                <h2>Home card content</h2>
                <h3>Brand assets</h3>
                <Row>
                  <Col md={6}>
                    <Field
                      label="Profile image URL"
                      name="profile_picture"
                      value={bioForm.profile_picture}
                      onChange={handleBioChange}
                    />
                    <UploadField
                      label="Upload profile image"
                      uploading={uploadingField === "profile_picture"}
                      onSelect={(event) =>
                        uploadBioImage(
                          "profile_picture",
                          event.target.files?.[0],
                          "profile-images"
                        )
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Footer / copyright logo URL"
                      name="copyrightLogo"
                      value={bioForm.copyrightLogo}
                      onChange={handleBioChange}
                    />
                    <UploadField
                      label="Upload footer / copyright logo"
                      uploading={uploadingField === "copyrightLogo"}
                      onSelect={(event) =>
                        uploadBioImage(
                          "copyrightLogo",
                          event.target.files?.[0],
                          "footer-logos"
                        )
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field
                      label="Header / brand logo URL"
                      name="profile_logo"
                      value={bioForm.profile_logo}
                      onChange={handleBioChange}
                    />
                    <UploadField
                      label="Upload header / brand logo"
                      uploading={uploadingField === "profile_logo"}
                      onSelect={(event) =>
                        uploadBioImage(
                          "profile_logo",
                          event.target.files?.[0],
                          "logos"
                        )
                      }
                    />
                  </Col>
                </Row>
                <h3>Identity</h3>
                <Row>
                  <Col md={6}>
                    <Field label="Name" name="name" value={bioForm.name} onChange={handleBioChange} />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Credential title"
                      name="credentialTitle"
                      value={bioForm.credentialTitle}
                      onChange={handleBioChange}
                      placeholder="Doctor of Science (Automation Technology)"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field
                      label="Position"
                      name="position"
                      value={bioForm.position}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
                <Field
                  label="Location"
                  name="location"
                  value={bioForm.location}
                  onChange={handleBioChange}
                />
                <Field
                  label="Short description"
                  name="description"
                  value={bioForm.description}
                  onChange={handleBioChange}
                  as="textarea"
                  rows={4}
                />
                <h3>Buttons and socials</h3>
                <Row>
                  <Col md={6}>
                    <Field
                      label="Booking URL"
                      name="bookingLink"
                      value={bioForm.bookingLink}
                      onChange={handleBioChange}
                      placeholder="Calendly or another booking link"
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Substack URL"
                      name="substackLink"
                      value={bioForm.substackLink}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field
                      label="CV URL"
                      name="cvLink"
                      value={bioForm.cvLink}
                      onChange={handleBioChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Current project URL"
                      name="currentProjectLink"
                      value={bioForm.currentProjectLink}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field label="Email" name="email" value={bioForm.email} onChange={handleBioChange} />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="LinkedIn URL"
                      name="linkedIn"
                      value={bioForm.linkedIn}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field
                      label="GitHub URL"
                      name="github"
                      value={bioForm.github}
                      onChange={handleBioChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Twitter URL"
                      name="twitter"
                      value={bioForm.twitter}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Field
                      label="Orcid URL"
                      name="collegeId"
                      value={bioForm.collegeId}
                      onChange={handleBioChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Google Scholar URL"
                      name="googleScholar"
                      value={bioForm.googleScholar}
                      onChange={handleBioChange}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
            <Col lg={5}>
              <div className="admin-panel admin-preview-panel">
                <h2>Image preview</h2>
                <div className="admin-portrait-preview">
                  <img src={previewImage} alt="Profile preview" />
                </div>
                <p className="admin-preview-caption">
                  Use a direct image URL. The public page will crop it automatically for the hero and about card.
                </p>
              </div>
            </Col>
          </Row>
        ) : null}

        {!loading && activeTab === "about" ? (
          <div className="admin-panel">
            <h2>About page content</h2>
            <Row>
              <Col md={6}>
                <Field
                  label="About heading"
                  name="aboutHeading"
                  value={bioForm.aboutHeading}
                  onChange={handleBioChange}
                  placeholder="Optional heading override"
                />
              </Col>
              <Col md={6}>
                <Field
                  label="About image URL"
                  name="aboutImage"
                  value={bioForm.aboutImage}
                  onChange={handleBioChange}
                />
                <UploadField
                  label="Upload about image"
                  uploading={uploadingField === "aboutImage"}
                  onSelect={(event) =>
                    uploadBioImage(
                      "aboutImage",
                      event.target.files?.[0],
                      "about-images"
                    )
                  }
                />
              </Col>
            </Row>
            <Field
              label="About intro"
              name="aboutIntro"
              value={bioForm.aboutIntro}
              onChange={handleBioChange}
              as="textarea"
              rows={3}
            />
            <Field
              label="About paragraphs"
              name="aboutParagraphsText"
              value={bioForm.aboutParagraphsText}
              onChange={handleBioChange}
              as="textarea"
              rows={10}
              placeholder="Separate paragraphs with a blank line"
            />
            <Field
              label="Focus areas"
              name="focusAreasText"
              value={bioForm.focusAreasText}
              onChange={handleBioChange}
              as="textarea"
              rows={5}
              placeholder="One focus item per line"
            />
          </div>
        ) : null}

        {!loading && activeTab === "papers" ? (
          <div className="admin-panel">
            <div className="admin-panel-header">
              <div>
                <h2>Research cards</h2>
                <p>{paperCountLabel}</p>
              </div>
              <Button className="admin-secondary-btn" onClick={addPaper}>
                Add paper
              </Button>
            </div>
            <div className="paper-editor-list">
              {papersForm.map((paper, index) => (
                <div className="paper-editor-card" key={paper.id || index}>
                  <div className="paper-editor-top">
                    <h3>{paper.title || `Paper ${index + 1}`}</h3>
                    <Button
                      variant="outline-danger"
                      className="paper-remove-btn"
                      onClick={() => removePaper(index)}
                    >
                      Remove
                    </Button>
                  </div>
                  <Row>
                    <Col md={8}>
                      <Field
                        label="Title"
                        name="title"
                        value={paper.title}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                    <Col md={4}>
                      <Field
                        label="Published on"
                        name="publishedOn"
                        value={paper.publishedOn}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                  </Row>
                  <Field
                    label="Authors"
                    name="authors"
                    value={paper.authors}
                    onChange={(event) => handlePaperChange(index, event)}
                  />
                  <Field
                    label="Abstract"
                    name="abstract"
                    value={paper.abstract}
                    onChange={(event) => handlePaperChange(index, event)}
                    as="textarea"
                    rows={5}
                  />
                  <Field
                    label="Card image URL"
                    name="image"
                    value={paper.image}
                    onChange={(event) => handlePaperChange(index, event)}
                  />
                  <UploadField
                    label="Upload card image"
                    uploading={uploadingField === `paper-${index}`}
                    onSelect={(event) =>
                      uploadPaperImage(index, event.target.files?.[0])
                    }
                  />
                  <Row>
                    <Col md={6}>
                      <Field
                        label="Paper URL"
                        name="paperLink"
                        value={paper.paperLink}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                    <Col md={6}>
                      <Field
                        label="Arxiv URL"
                        name="arvixLink"
                        value={paper.arvixLink}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Field
                        label="Code URL"
                        name="sourceCodeLink"
                        value={paper.sourceCodeLink}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                    <Col md={6}>
                      <Field
                        label="Slides URL"
                        name="presentationSlidesLink"
                        value={paper.presentationSlidesLink}
                        onChange={(event) => handlePaperChange(index, event)}
                      />
                    </Col>
                  </Row>
                  <Field
                    label="Video URL"
                    name="presentationVideoLink"
                    value={paper.presentationVideoLink}
                    onChange={(event) => handlePaperChange(index, event)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Container>
    </div>
  );
}
